import { useDeletePresentationMutation } from '@carboncopyco/data';
import {
  ArchiveIcon,
  DeleteIcon,
  MoreVerticalOutlineIcon,
  PlayIcon,
  ShowOutlineIcon,
} from '@carboncopyco/icons';
import type { EnhancedPresentation } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  ConfirmAlert,
  Dropdown,
  DropdownItem,
  TooltipBubble,
  notify,
} from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { usePresentationPreview } from '../../usePresentationPreview';
import { useOverlayPageProps } from '../OverlayPageContext';

interface PresentationRowButtonsProps {
  presentation: EnhancedPresentation;
}

export const PresentationRowButtons = ({
  presentation,
}: PresentationRowButtonsProps) => {
  const { trackEvent } = useAnalytics();
  const { goToDemoPreview, goToPresentation, organizationId } =
    useOverlayPageProps();
  const [archivePresentation] = useDeletePresentationMutation();
  const [
    showArchiveModal,
    { setTrue: setTruesShowArchiveModal, setFalse: setFalseShowArchiveModal },
  ] = useBoolean();
  const location = 'demo_info_page_presentations_tab_table_side_actions';
  const handlePreview = usePresentationPreview({
    presentation,
    organizationId,
    goToDemoPreview,
    from: 'presentation',
  });

  return (
    <div className="flex items-center gap-3">
      <TooltipBubble
        placement="top"
        title="Preview"
      >
        <Button
          aria-label="preview"
          dark
          icon={<ShowOutlineIcon color={ColorHex.natural600} />}
          onClick={() => {
            trackEvent(AnalyticsTrackEventName.DEMO_CLICK_PREVIEW, {
              location,
            });
            handlePreview();
          }}
        />
      </TooltipBubble>
      <TooltipBubble
        placement="top"
        title="Present now"
      >
        <Button
          aria-label="Present button"
          dark
          icon={<PlayIcon color={ColorHex.natural600} />}
          onClick={() => {
            trackEvent(AnalyticsTrackEventName.DEMO_CLICK_PRESENT, {
              location,
            });
            goToPresentation(presentation);
          }}
        />
      </TooltipBubble>
      <Dropdown
        asChild
        side="left"
        triggerElement={
          <div>
            <TooltipBubble
              placement="top"
              title="More actions"
            >
              <Button
                aria-label="actions"
                dark
                icon={<MoreVerticalOutlineIcon color={ColorHex.natural600} />}
              />
            </TooltipBubble>
          </div>
        }
      >
        <DropdownItem
          iconLeft={<ArchiveIcon size={18} />}
          label="Archive"
          onClick={() => {
            trackEvent(
              AnalyticsTrackEventName.DEMO_CLICK_ARCHIVE_PRESENTATION,
              {
                location,
              }
            );
            setTruesShowArchiveModal();
          }}
        />
      </Dropdown>
      {showArchiveModal && (
        <ConfirmAlert
          title="Archive presentation"
          description="Archiving will remove this presentation from your environment"
          containerClass="w-ds-normal-modal"
          danger
          yesIcon={<DeleteIcon />}
          yesLabel="Archive"
          show={showArchiveModal}
          onClose={() => {
            trackEvent(
              AnalyticsTrackEventName.DEMO_CLICK_ARCHIVE_PRESENTATION_CANCEL,
              {
                location: 'demo_info_page_archive_presentation_modal',
              }
            );
            setFalseShowArchiveModal();
          }}
          onConfirm={() => {
            trackEvent(
              AnalyticsTrackEventName.DEMO_CLICK_ARCHIVE_PRESENTATION_APPROVE,
              {
                location: 'demo_info_page_archive_presentation_modal',
              }
            );
            archivePresentation({
              organizationId,
              id: String(presentation.id),
            })
              .unwrap()
              .then(() => notify.success('Archived presentation successfully'))
              .catch(() =>
                notify.error('There was an error archiving this presentation')
              )
              .finally(setFalseShowArchiveModal);
          }}
        />
      )}
    </div>
  );
};
